import {isMetaProfile} from '../../profile'
import {generateMaid, MaidLandingParameters} from './maid'

interface LandingParameters extends MaidLandingParameters {
  label?: string
  gclid?: string
  msclkid?: string
  utm_source?: string
  utm_referrer?: string
  pac?: string
}

/**
 * Pure function to generate a sapiLabel from the provided context.
 * This is used in app and web contexts, please do not colocate with browser-specific implementations (ie. cookies)
 * @param props -
 * @param props.landingParameters -
 * @param props.anonymousId -
 * @param props.partnerId - The brand code, which is determined by the instance of the website that is making requests to SAPI.
 * @param props.persistedMaid -
 */
export const generateEncodedSapiLabel = ({
  landingParameters,
  anonymousId,
  partnerId,
  persistedMaid,
  persistedLabel
}: {
  landingParameters: LandingParameters
  anonymousId: string
  partnerId?: string | undefined
  persistedMaid?: string | undefined | null
  persistedLabel?: string | undefined | null
}) => {
  const trafficParameterKeys = ['label', 'gclid', 'msclkid'] as const

  const hasTrafficParam = (
    [...trafficParameterKeys, 'utm_source'] as const
  ).some(param => !!landingParameters[param])

  if (!hasTrafficParam && persistedLabel) {
    return persistedLabel
  }

  const labels = trafficParameterKeys.reduce((prev, curr) => {
    const value = landingParameters[curr]
    if (value) prev.push(`${curr}=${encodeURIComponent(value.toString())}`)
    return prev
  }, [] as string[])

  if (partnerId) {
    labels.push(`utm_source=${partnerId}`)
  } else {
    if (landingParameters['utm_source']) {
      labels.push(
        `utm_source=${encodeURIComponent(
          landingParameters['utm_source'].toString()
        )}`
      )
    }
  }

  // Kept for white labels too: utm_source is replaced by partnerId there, but RAA still needs the originating channel (e.g. gha)
  if (landingParameters['utm_referrer']) {
    labels.push(
      `utm_referrer=${encodeURIComponent(
        landingParameters['utm_referrer'].toString()
      )}`
    )
  }

  // Price-accuracy check flag from meta partners (e.g. Skyscanner); SAPI uses it to skip ACL
  if (landingParameters['pac']) {
    labels.push(
      `pac=${encodeURIComponent(landingParameters['pac'].toString())}`
    )
  }

  const {profile} = landingParameters
  const isMetaTraffic = isMetaProfile(profile)

  let maid = persistedMaid
  // Generate maid using hotelId, checkIn and checkOut if it's a meta traffic
  // Or generate maid when it is empty
  if (isMetaTraffic || !persistedMaid) {
    maid = generateMaid({anonymousId, landingParameters})
  }

  labels.push(`maid=${maid}`)

  return labels.join('&')
}
